/**
 * Panda Note configuration backup (`.jnbundle`) — import side.
 *
 * The file is a single JSON document produced by the Mac app's
 * `ConfigBackupCodec` (Sources/JNote/Settings/ConfigBackup.swift):
 *
 * - Plaintext header fields — format id, bundle version, exported-at, app
 *   version/platform, section list. Enough for a summary before the user
 *   types the passphrase; nothing user-specific leaks.
 * - `envelope` — the whole payload sealed with AES-256-GCM under a
 *   PBKDF2-SHA256 key (self-describing, so cost parameters can move).
 *
 * Unknown or newer bundle versions are rejected outright — no degraded or
 * partial imports. Mirrors JellyBot's `.jbbundle` design, adapted to a
 * KDF every platform speaks natively (CommonCrypto / node:crypto /
 * WebCrypto).
 */

export const BUNDLE_FORMAT = 'jnote-backup';
export const BUNDLE_VERSION = 1;
export const BUNDLE_EXTENSION = '.jnbundle';

export type BackupSectionName = 'preferences' | 'llm' | 'cloudSync' | 'mcp';
export const BACKUP_SECTION_NAMES: readonly BackupSectionName[] = [
  'preferences',
  'llm',
  'cloudSync',
  'mcp',
];

export interface BackupEnvelope {
  v: number;
  kdf: string;
  pbkdf2: {
    iterations: number;
    hash: string;
    saltBytes: number;
    keyBytes: number;
  };
  cipher: string;
  /** base64 — 16 random bytes. */
  salt: string;
  /** base64 — 12-byte GCM nonce. */
  iv: string;
  /** base64 — 16-byte GCM auth tag. */
  authTag: string;
  /** base64 — sealed payload JSON. */
  data: string;
}

export interface BackupHeader {
  format: string;
  version: number;
  /** ISO-8601 UTC. */
  exportedAt: string;
  appVersion: string;
  /** "macos" or "web". */
  appPlatform: string;
  sections: BackupSectionName[];
}

/** Parsed `.jnbundle` file: header + still-sealed envelope. */
export interface BackupFile {
  header: BackupHeader;
  envelope: BackupEnvelope;
}

export interface BackupPreferences {
  language?: string;
  contentMode?: string;
  sidebarWidth?: number;
  editorFontSize?: number;
  dictationShortcutKey?: string;
  dictationShortcutModifiers?: number;
  onboardingCompleted?: boolean;
}

export interface BackupLLM {
  baseURL: string;
  apiKey: string;
  model: string;
  triggerRatio: number;
  contextLimit: number;
}

export interface BackupCloudSync {
  serverBaseURL: string;
  serverPassword: string;
  isEnabled: boolean;
}

export interface BackupMCP {
  servers: unknown[];
}

export interface BackupPayload {
  preferences?: BackupPreferences | null;
  llm?: BackupLLM | null;
  cloudSync?: BackupCloudSync | null;
  mcp?: BackupMCP | null;
}

/** The file is structurally invalid (not our JSON / bad header / bad version). */
export class ConfigBackupFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigBackupFormatError';
  }
}

/** Wrong passphrase, or a tampered/corrupted envelope (indistinguishable). */
export class ConfigBackupPassphraseError extends Error {
  constructor(message = 'Wrong passphrase, or the backup file is corrupted.') {
    super(message);
    this.name = 'ConfigBackupPassphraseError';
  }
}

const MAX_ITERATIONS = 10_000_000;
const MIN_ITERATIONS = 10_000;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseEnvelope(value: unknown): BackupEnvelope {
  const raw = asRecord(value);
  if (!raw) throw new ConfigBackupFormatError('Backup envelope is missing');
  const params = asRecord(raw.pbkdf2);
  const iterations: unknown = params?.iterations;
  const saltBytes: unknown = params?.saltBytes;
  if (
    raw.v !== 1 ||
    raw.kdf !== 'pbkdf2-sha256' ||
    raw.cipher !== 'aes-256-gcm' ||
    !params ||
    params.hash !== 'sha256' ||
    params.keyBytes !== 32 ||
    typeof iterations !== 'number' ||
    !Number.isInteger(iterations) ||
    iterations < MIN_ITERATIONS ||
    iterations > MAX_ITERATIONS ||
    typeof saltBytes !== 'number' ||
    !Number.isInteger(saltBytes) ||
    saltBytes < 8 ||
    saltBytes > 64
  ) {
    throw new ConfigBackupPassphraseError('Unsupported backup envelope format');
  }
  for (const field of ['salt', 'iv', 'authTag', 'data'] as const) {
    if (typeof raw[field] !== 'string' || (raw[field] as string).length === 0) {
      throw new ConfigBackupPassphraseError(
        `Backup envelope field "${field}" is missing`,
      );
    }
  }
  return {
    v: 1,
    kdf: 'pbkdf2-sha256',
    pbkdf2: {
      iterations,
      hash: 'sha256',
      saltBytes,
      keyBytes: 32,
    },
    cipher: 'aes-256-gcm',
    salt: raw.salt as string,
    iv: raw.iv as string,
    authTag: raw.authTag as string,
    data: raw.data as string,
  };
}

/**
 * Parses + validates a `.jnbundle` file (the raw text of the picked file).
 * Throws `ConfigBackupFormatError` for anything that is not a version-1
 * Panda Note backup.
 */
export function parseBackupFile(text: string): BackupFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ConfigBackupFormatError('This file is not a Panda Note backup.');
  }
  const raw = asRecord(parsed);
  if (!raw) {
    throw new ConfigBackupFormatError('This file is not a Panda Note backup.');
  }
  if (raw.format !== BUNDLE_FORMAT) {
    throw new ConfigBackupFormatError('This file is not a Panda Note backup.');
  }
  if (raw.version !== BUNDLE_VERSION) {
    throw new ConfigBackupFormatError(
      `Unsupported backup version ${String(raw.version)} — this app supports version ${BUNDLE_VERSION}.`,
    );
  }
  if (
    typeof raw.exportedAt !== 'string' ||
    raw.exportedAt.length === 0 ||
    typeof raw.appVersion !== 'string' ||
    raw.appVersion.length === 0 ||
    !Array.isArray(raw.sections) ||
    raw.sections.length === 0 ||
    !raw.sections.every((name) => BACKUP_SECTION_NAMES.includes(name as BackupSectionName))
  ) {
    throw new ConfigBackupFormatError('This file is not a Panda Note backup.');
  }
  const platform = raw.appPlatform === 'web' ? 'web' : 'macos';
  return {
    header: {
      format: BUNDLE_FORMAT,
      version: BUNDLE_VERSION,
      exportedAt: raw.exportedAt,
      appVersion: raw.appVersion,
      appPlatform: platform,
      sections: raw.sections as BackupSectionName[],
    },
    envelope: parseEnvelope(raw.envelope),
  };
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Strict shape check of a decrypted payload; absent sections stay absent. */
export function normalizeBackupPayload(value: unknown): BackupPayload {
  const raw = asRecord(value);
  if (!raw) {
    throw new ConfigBackupFormatError('Backup payload is not an object');
  }

  const payload: BackupPayload = {};

  if (raw.preferences != null) {
    const prefs = asRecord(raw.preferences);
    if (!prefs) throw new ConfigBackupFormatError('Section "preferences" has an invalid shape');
    const out: BackupPreferences = {};
    const language = prefs.language;
    const contentMode = prefs.contentMode;
    const dictationKey = prefs.dictationShortcutKey;
    if (typeof language === 'string') out.language = language;
    if (typeof contentMode === 'string') out.contentMode = contentMode;
    if (typeof dictationKey === 'string') out.dictationShortcutKey = dictationKey;
    const sidebarWidth = prefs.sidebarWidth;
    const editorFontSize = prefs.editorFontSize;
    if (isFiniteNumber(sidebarWidth)) out.sidebarWidth = sidebarWidth;
    if (isFiniteNumber(editorFontSize)) out.editorFontSize = editorFontSize;
    const dictationModifiers = prefs.dictationShortcutModifiers;
    if (isFiniteNumber(dictationModifiers)) {
      out.dictationShortcutModifiers = dictationModifiers;
    }
    if (typeof prefs.onboardingCompleted === 'boolean') {
      out.onboardingCompleted = prefs.onboardingCompleted;
    }
    payload.preferences = out;
  }

  if (raw.llm != null) {
    const llm = asRecord(raw.llm);
    const baseURL = llm?.baseURL;
    const apiKey = llm?.apiKey;
    const model = llm?.model;
    const triggerRatio = llm?.triggerRatio;
    const contextLimit: unknown = llm?.contextLimit;
    if (
      typeof baseURL !== 'string' ||
      typeof apiKey !== 'string' ||
      typeof model !== 'string' ||
      !isFiniteNumber(triggerRatio) ||
      typeof contextLimit !== 'number' ||
      !Number.isInteger(contextLimit)
    ) {
      throw new ConfigBackupFormatError('Section "llm" has an invalid shape');
    }
    payload.llm = { baseURL, apiKey, model, triggerRatio, contextLimit };
  }

  if (raw.cloudSync != null) {
    const sync = asRecord(raw.cloudSync);
    const serverBaseURL = sync?.serverBaseURL;
    const serverPassword = sync?.serverPassword;
    const isEnabled = sync?.isEnabled;
    if (
      typeof serverBaseURL !== 'string' ||
      typeof serverPassword !== 'string' ||
      typeof isEnabled !== 'boolean'
    ) {
      throw new ConfigBackupFormatError('Section "cloudSync" has an invalid shape');
    }
    payload.cloudSync = { serverBaseURL, serverPassword, isEnabled };
  }

  if (raw.mcp != null) {
    const mcp = asRecord(raw.mcp);
    if (!mcp || !Array.isArray(mcp.servers) || mcp.servers.length > 500) {
      throw new ConfigBackupFormatError('Section "mcp" has an invalid shape');
    }
    payload.mcp = { servers: mcp.servers };
  }

  if (
    payload.preferences == null &&
    payload.llm == null &&
    payload.cloudSync == null &&
    payload.mcp == null
  ) {
    throw new ConfigBackupFormatError('Backup payload has no sections');
  }
  return payload;
}
